import {ChangeDetectorRef, Component, Input, OnInit} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Cart} from '@spartacus/cart/base/root';
import {Address, B2BUser, User} from '@spartacus/core';
import {combineLatest, Observable, of} from 'rxjs';
import {catchError, first, map, switchMap, take, tap} from 'rxjs/operators';
import {DcpApiService} from '@dcp/configurator';
import {PaymentMethods, RestrictedPaymentTypesResponse} from '@app/model/dcp-config-service/basestores-country.model';
import {AddressUsage} from '@app/model/order/dcp-order-entry.model';
import {PaymentTypeCode} from '@app/model/order/dcp-order.model';
import {DcpCheckoutPaymentService} from '@app/payment/services/dcp-checkout-payment.service';
import {BaseStoreService} from '@app/services/base-store.service';
import {DcpActiveCartService} from '@app/services/dcp-active-cart.service';
import {DcpCcPspService, PaymentProvider} from '@app/payment/services/dcp-cc-psp.service';
import {OrderEntry} from '@spartacus/cart/base/root/models/cart.model';

@UntilDestroy()
@Component({
  selector: 'dcp-payment-method-display',
  templateUrl: './payment-method-display.component.html',
  styleUrls: ['./payment-method-display.component.scss']
})
export class PaymentMethodDisplayComponent implements OnInit {

  @Input() b2bUnitAddresses$: Observable<Address[]>;
  @Input() b2bUnitContacts$: Observable<B2BUser[]>;
  @Input() currentUser: User;
  b2bUnitsContacts: B2BUser[];
  invoiceAddresses: Address[];
  companyAddress: Address;
  PaymentTypeCodeReference = PaymentTypeCode;
  paymentFormGroup: UntypedFormGroup = new UntypedFormGroup({
    paymentType: new UntypedFormControl(PaymentTypeCode.CARD),
  });
  isCreditCardShown: boolean = false;
  isInvoiceShown: boolean = false;
  /** If one of payments methods is excluded by config */
  isOneOfPaymentsMethodExcluded: boolean;
  /** Payment methods configured for the store in commerce cloud, excluding restricted by config service */
  private baseStoreAllowedPaymentsMethods: PaymentMethods[] = [];

  constructor(
    private dcpApiService: DcpApiService,
    private cdr: ChangeDetectorRef,
    private dcpActiveCartService: DcpActiveCartService,
    private dcpCheckoutPaymentService: DcpCheckoutPaymentService,
    private baseStoreService: BaseStoreService,
    private dcpCcPspService: DcpCcPspService,
    private activatedRoute: ActivatedRoute) {
  }

  ngOnInit() {
    this.initData();
    this.initInvoiceAddresses();
    this.initInvoiceContacts();
    this.checkFormChanges();
  }

  get paymentTypeControl() {
    return this.paymentFormGroup.get('paymentType')
  }

  /**
   * Preselect payment type if available for store
   * From paymentType with first priority
   * From cart with second priority
   * or CreditCard as default if available
   */
  preselectPaymentMethod() {
    if (this.isCreditCardShown) {
      this.dcpActiveCartService.getActiveCart().pipe(take(1), untilDestroyed(this)).subscribe((cart: Cart) => {
        // user previously selected PaymentTypeCode
        const cartPaymentTypeCode: PaymentTypeCode = PaymentTypeCode[cart?.paymentType?.code];
        // queryParam PaymentTypeCode
        const urlPaymentTypeCode: PaymentTypeCode = PaymentTypeCode[this.getPaymentTypeFromUrl()];
        const urlPaymentType: PaymentTypeCode = this.isPaymentTypeAllowedForCurrentStore(urlPaymentTypeCode) ? urlPaymentTypeCode : undefined;
        const previouslySelectedPaymentType: PaymentTypeCode = this.isPaymentTypeAllowedForCurrentStore(cartPaymentTypeCode) ? cartPaymentTypeCode : undefined;

        const patchValue: string = urlPaymentType || previouslySelectedPaymentType || this.getDefaultPaymentType();
        this.paymentTypeControl.patchValue(patchValue);
      });
    } else {
      this.paymentTypeControl.patchValue(PaymentTypeCode.ACCOUNT);
    }
  }

  setPaymentMethodOnEvent($event: Event, value: string) {
    $event.preventDefault();
    this.paymentTypeControl.setValue(value);

  }

  initInvoiceAddresses() {
    this.b2bUnitAddresses$.pipe(untilDestroyed(this)).subscribe((b2bUnitAddresses: Address[]) => {
      this.invoiceAddresses = b2bUnitAddresses.filter((address: Address) => address.addressUsage.includes(AddressUsage.BILL_TO));
      this.companyAddress = b2bUnitAddresses.find((address: Address) => address.addressUsage.includes(AddressUsage.SOLD_TO));
      if (this.companyAddress && !this.invoiceAddresses.some(address => address.sapAddressUUID === this.companyAddress.sapAddressUUID)) {
        this.invoiceAddresses.unshift(this.companyAddress);
      }
      this.invoiceAddresses = this.sortAddressesAndContacts(this.invoiceAddresses, 'addressUsage', AddressUsage.SOLD_TO);
      this.cdr.detectChanges();
    });
  }

  initInvoiceContacts() {
    this.b2bUnitContacts$.pipe(untilDestroyed(this)).subscribe((b2bUnitContacts: B2BUser[]) => {
      this.b2bUnitsContacts = b2bUnitContacts;
      this.b2bUnitsContacts = this.sortAddressesAndContacts(this.b2bUnitsContacts, 'uid', this.currentUser.uid);
      const isCurrentUserIncluded = this.b2bUnitsContacts.some(contact => contact.uid === this.currentUser.uid);
      if (!isCurrentUserIncluded) {
        this.b2bUnitsContacts.unshift(this.currentUser);
      }
      this.cdr.detectChanges();
    });
  }

  sortAddressesAndContacts<T>(unsortedArray: T[], property, value): T[] {
    return unsortedArray.sort(item => {
      if (item[property] === value) {
        return -1;
      }
    });
  }

  initData() {
    combineLatest([
      this.baseStoreService.getCurrentBaseStore(),
      this.dcpCcPspService.getCurrentCartPaymentProviderId(),
      this.dcpActiveCartService.getActiveCart(),
    ])
      .pipe(
        take(1),
        tap(([baseStore, cartPaymentProvider, cart]) => {
          const allowedPaymentMethods = this.getAllowedPaymentMethods(cart.entries[0]);
          if (Array.isArray(allowedPaymentMethods) && allowedPaymentMethods.length) {
            this.baseStoreAllowedPaymentsMethods = allowedPaymentMethods;
          } else {
            this.baseStoreAllowedPaymentsMethods = baseStore.deliveryModes.deliveryModes
              .flatMap(deliveryMode => deliveryMode.supportedPaymentModes.paymentModes)
              .map(paymentMode => (paymentMode.code as PaymentMethods));
          }
          this.isInvoiceShown = this.baseStoreAllowedPaymentsMethods.includes(PaymentMethods.INVOICE);
          const isCartProviderSupported: boolean = this.dcpCcPspService.getSupportedPaymentProviders().includes(cartPaymentProvider);

          if (this.baseStoreAllowedPaymentsMethods.includes(PaymentMethods.CREDITCARD) && isCartProviderSupported) {
            // call /cardConfig only when other conditions are met
            this.isCardConfigPresent().pipe(untilDestroyed(this)).subscribe(cardConfig => {
              this.isCreditCardShown = cardConfig;
            })
          } else {
            this.isCreditCardShown = false;
          }
        }),
        switchMap(() => this.dcpApiService.callKyma('config', 'restrictedPaymentTypes', {}))
      ).pipe(untilDestroyed(this)).subscribe((res: RestrictedPaymentTypesResponse) => {
      const excludedPaymentTypes: string[] = res.restrictedPaymentTypes[this.currentUser?.companyType];
      this.baseStoreAllowedPaymentsMethods = this.baseStoreAllowedPaymentsMethods.filter(paymentType => !excludedPaymentTypes.includes(paymentType));
      this.isInvoiceShown = this.isInvoiceShown && this.baseStoreAllowedPaymentsMethods.includes(PaymentMethods.INVOICE);
      this.isCreditCardShown = this.isCreditCardShown && this.baseStoreAllowedPaymentsMethods.includes(PaymentMethods.CREDITCARD);
      this.isOneOfPaymentsMethodExcluded = !!excludedPaymentTypes?.length;
      this.preselectPaymentMethod();
      this.cdr.detectChanges();
    });
  }

  getAllowedPaymentMethods(entry: OrderEntry): PaymentMethods[] {
    let allowedPaymentMethods;
    try {
      allowedPaymentMethods = JSON.parse(entry.data.find(e => e.type === 'salesArea').value).allowedPaymentMethods;
    } catch (err) {
    }
    return allowedPaymentMethods;
  }

  isCardConfigPresent(): Observable<boolean> {
    return combineLatest([
      this.dcpCcPspService.getCurrentCartPaymentProviderId(),
      this.dcpActiveCartService.getActiveCart()
    ]).pipe(
      first(),
      switchMap(([paymentProviderId, cart]: [PaymentProvider, Cart]) => {
        const {vendor, salesOrg} = this.dcpCheckoutPaymentService.getVendorSalesOrg(cart);
        return this.dcpCheckoutPaymentService.getCardConfig(vendor, salesOrg, paymentProviderId);
      }),
      map(response => true),
      catchError((err) => of(false))
    );
  }

  checkFormChanges() {
    this.paymentFormGroup.valueChanges.pipe(untilDestroyed(this))
      .subscribe((form) => {
        this.dcpCheckoutPaymentService.setCurrentPaymentType(form.paymentType);
      });
  }

  /**
   * PaymentTypeCode.CARD is default payment type
   * if available for the store
   *
   * @private
   */
  private getDefaultPaymentType(): string {
    return this.isCreditCardShown ? PaymentTypeCode.CARD : PaymentTypeCode.ACCOUNT;
  }

  private getPaymentTypeFromUrl(): string {
    return this.activatedRoute.snapshot.queryParamMap.get('paymentType')?.toUpperCase();
  }

  /**
   * PaymentType is allowed if exist for current base sore
   * and not restricted by config
   *
   * @param code
   * @private
   */
  private isPaymentTypeAllowedForCurrentStore(code: PaymentTypeCode): boolean {
    return this.baseStoreAllowedPaymentsMethods.includes(this.baseStoreService.getAllowedPaymentMethodByType(code));
  }
}
